import { useState, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import * as Pop from '@radix-ui/react-popover';
import { X } from '@phosphor-icons/react';
import { Button, IconButton, cx } from './core';

/** Side panel for editing without losing the list (preferred over modals for tasks). */
export function Drawer({ open, onOpenChange, title, description, children, footer, width = 560 }: {
  open: boolean; onOpenChange: (v: boolean) => void; title: ReactNode; description?: ReactNode; children: ReactNode; footer?: ReactNode; width?: number;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="overlay fixed inset-0 z-[50] bg-[var(--m-scrim)]" />
        <Dialog.Content aria-describedby={description ? undefined : undefined}
          className="drawer-panel fixed inset-y-0 left-0 z-[60] flex w-full flex-col bg-canvas shadow-[0_0_0_1px_var(--m-line),0_24px_60px_var(--m-shadow)] outline-none"
          style={{ maxWidth: width }}>
          <div className="flex items-start justify-between gap-4 border-b border-line px-6 py-4">
            <div className="min-w-0">
              <Dialog.Title className="text-base font-medium">{title}</Dialog.Title>
              {description ? <Dialog.Description className="mt-0.5 text-[13px] text-muted">{description}</Dialog.Description> : <Dialog.Description className="sr-only">{String(title)}</Dialog.Description>}
            </div>
            <Dialog.Close asChild><IconButton label="بستن"><X size={18} /></IconButton></Dialog.Close>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
          {footer && <div className="flex items-center justify-end gap-2 border-t border-line bg-surface px-6 py-3">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Confirmation only for destructive or irreversible actions; names the object and the consequence. */
export function Confirm({ trigger, title, body, confirmLabel, onConfirm, tone = 'danger' }: {
  trigger: ReactNode; title: string; body?: ReactNode; confirmLabel: string; onConfirm: () => Promise<unknown> | void; tone?: 'danger' | 'primary';
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="overlay fixed inset-0 z-[50] bg-[var(--m-scrim)]" />
        <Dialog.Content className="pop fixed left-1/2 top-1/2 z-[60] w-[min(440px,92vw)] -translate-x-1/2 -translate-y-1/2 rounded-control border border-line bg-surface p-6 shadow-[0_24px_60px_var(--m-shadow)] outline-none">
          <Dialog.Title className="text-base font-medium">{title}</Dialog.Title>
          <Dialog.Description asChild><div className="mt-2 text-muted">{body}</div></Dialog.Description>
          <div className="mt-6 flex justify-end gap-2">
            <Dialog.Close asChild><Button>انصراف</Button></Dialog.Close>
            <Button variant={tone === 'danger' ? 'danger' : 'primary'} loading={busy}
              onClick={async () => { setBusy(true); try { await onConfirm(); setOpen(false); } finally { setBusy(false); } }}>{confirmLabel}</Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function Menu({ trigger, items }: { trigger: ReactNode; items: ({ label: ReactNode; icon?: ReactNode; onSelect: () => void; danger?: boolean; disabled?: boolean } | 'sep')[] }) {
  return (
    <Dropdown.Root dir="rtl">
      <Dropdown.Trigger asChild>{trigger}</Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content align="end" sideOffset={6} className="pop z-[70] min-w-48 rounded-control border border-line bg-surface p-1 shadow-[0_12px_32px_var(--m-shadow)]">
          {items.map((i, k) => i === 'sep'
            ? <Dropdown.Separator key={k} className="my-1 h-px bg-line" />
            : (
              <Dropdown.Item key={k} disabled={i.disabled} onSelect={i.onSelect}
                className={cx('flex h-9 cursor-pointer select-none items-center gap-2 rounded-[1px] px-2.5 text-sm outline-none data-[disabled]:opacity-40 data-[highlighted]:bg-sunken', i.danger && 'text-danger')}>
                {i.icon}{i.label}
              </Dropdown.Item>
            ))}
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}

export function Popover({ trigger, children, open, onOpenChange, align = 'start', className }: { trigger: ReactNode; children: ReactNode; open?: boolean; onOpenChange?: (v: boolean) => void; align?: 'start' | 'end' | 'center'; className?: string }) {
  return (
    <Pop.Root open={open} onOpenChange={onOpenChange}>
      <Pop.Trigger asChild>{trigger}</Pop.Trigger>
      <Pop.Portal>
        <Pop.Content align={align} sideOffset={6} collisionPadding={12}
          className={cx('pop z-[70] rounded-control border border-line bg-surface p-3 shadow-[0_12px_32px_var(--m-shadow)] outline-none', className)}>
          {children}
        </Pop.Content>
      </Pop.Portal>
    </Pop.Root>
  );
}
